/**
 * CpcSearchPage — two-pane workspace for CPC Classification Search.
 *
 * Route: /cpc-search/:subProjectId
 *
 * Layout mirrors WorkspacePage: WorkspaceHeader + Chat (~30%) | Tabbed right pane (~70%).
 * Right pane tabs: Features | Progress | CPC Table
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { subProjectService } from '../services/subProjectService';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import { cpcSearchService } from '../services/cpcSearchService';
import type { JobStatusResponse, CpcTable, CpcJobStatus, LogEntry, JobHistoryEntry } from '../services/cpcSearchService';
import useFeaturesListStore from '../stores/featuresListStore';
import WorkspaceHeader from '../components/WorkspaceHeader';
import SpeakingChatbotV2Assistant from '../components/SpeakingChatbotV2Assistant';
import CpcDownloadModal from '../components/CpcDownloadModal';

const POLL_INTERVAL_MS = 3000;

type Tab = 'features' | 'progress' | 'table';

// ── Log table ─────────────────────────────────────────────────────────────────

const KIND_LABEL: Record<string, string> = {
  pnc:            'PNC',
  retrieve:       'Retrieve',
  score:          'Score',
  top_classes:    'Top CPC',
  class_retrieve: 'Retrieve',
  class_score:    'Score',
};

const KIND_COLOR: Record<string, string> = {
  pnc:            'bg-slate-100 text-slate-600',
  retrieve:       'bg-blue-100 text-blue-700',
  score:          'bg-amber-100 text-amber-700',
  top_classes:    'bg-violet-100 text-violet-700',
  class_retrieve: 'bg-cyan-100 text-cyan-700',
  class_score:    'bg-green-100 text-green-700',
};

const PHASE_COLOR: Record<string, string> = {
  '1': 'bg-indigo-100 text-indigo-700',
  '2': 'bg-teal-100 text-teal-700',
  '3': 'bg-orange-100 text-orange-700',
};

/** True when an entry is an "in-progress" announcement (no final count yet). */
function isInProgress(entry: LogEntry): boolean {
  if (entry.kind === 'retrieve' && entry.docs_retrieved == null) return true;
  if (entry.kind === 'score' && entry.docs_relevant == null) return true;
  if (entry.kind === 'class_retrieve' && entry.docs_retrieved == null) return true;
  if (entry.kind === 'class_score' && entry.avg_score == null && entry.feature_name == null) return true;
  return false;
}

function LogRow({ entry }: { entry: LogEntry }) {
  const phaseLabel = `P${entry.phase}`;
  const kindLabel = KIND_LABEL[entry.kind] ?? entry.kind;
  const phaseCls = PHASE_COLOR[entry.phase] ?? 'bg-slate-100 text-slate-500';
  const kindCls = KIND_COLOR[entry.kind] ?? 'bg-slate-100 text-slate-500';
  const pending = isInProgress(entry);

  let detail = entry.msg;
  if (entry.kind === 'pnc' && entry.query) {
    const q = entry.query.length > 60 ? entry.query.slice(0, 58) + '…' : entry.query;
    detail = entry.count != null ? `${q}  →  ${entry.count} results` : q;
  } else if (entry.kind === 'retrieve') {
    if (entry.docs_retrieved != null) {
      const q = entry.query ? (entry.query.length > 46 ? entry.query.slice(0, 44) + '…' : entry.query) : '';
      detail = `${q}  →  ${entry.docs_retrieved} abstracts`;
    }
  } else if (entry.kind === 'score') {
    // leave detail = entry.msg (already contains distribution or top-3)
  } else if (entry.kind === 'top_classes' && entry.top_classes) {
    detail = entry.top_classes.length ? entry.top_classes.join('  ·  ') : `none — ${entry.msg}`;
  } else if (entry.kind === 'class_retrieve' && entry.docs_retrieved != null) {
    detail = `${entry.docs_retrieved} abstracts`;
  } else if (entry.kind === 'class_score' && entry.avg_score != null) {
    detail = `avg ${entry.avg_score.toFixed(1)}`;
  }

  const subject = entry.kind === 'class_retrieve' || entry.kind === 'class_score'
    ? (entry.cpc_class ?? '')
    : (entry.feature_name ?? '');

  const isEmptyTopClasses = entry.kind === 'top_classes' && entry.top_classes?.length === 0;
  const rowCls = isEmptyTopClasses
    ? 'bg-red-50/40 border-b border-red-100'
    : pending
      ? 'bg-amber-50/40 border-b border-slate-100'
      : 'hover:bg-slate-50/50 border-b border-slate-100';

  return (
    <tr className={`align-top ${rowCls}`}>
      <td className="px-2 py-1 font-mono text-[10px] text-slate-400 whitespace-nowrap">{entry.ts}</td>
      <td className="px-2 py-1">
        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${phaseCls}`}>{phaseLabel}</span>
      </td>
      <td className="px-2 py-1">
        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${kindCls}`}>{kindLabel}</span>
      </td>
      <td className="px-2 py-1 text-[11px] text-slate-600 max-w-[16ch] truncate font-medium" title={subject}>{subject}</td>
      <td className="px-2 py-1 text-[11px] font-mono" style={{ color: pending ? '#92400e' : isEmptyTopClasses ? '#b91c1c' : undefined }}>
        {pending && <span className="inline-block w-2 h-2 rounded-full bg-amber-400 animate-pulse mr-1.5 align-middle" />}
        {entry.kind === 'score' && entry.avg_score != null && (
          <span className={`inline-block px-1.5 py-0.5 rounded mr-2 text-[10px] font-bold ${entry.avg_score >= 50 ? 'bg-green-100 text-green-700' : entry.avg_score >= 25 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
            avg {entry.avg_score.toFixed(0)}
          </span>
        )}
        {detail}
      </td>
    </tr>
  );
}

function LogTable({ log, isRunning }: { log: LogEntry[]; isRunning: boolean }) {
  const logEndRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [log.length]);

  if (!log.length) return null;

  return (
    <div className="mt-3 border border-slate-200 rounded overflow-hidden">
      <div className="flex items-center justify-between bg-slate-50 px-2 py-1 border-b border-slate-200">
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Step log</span>
        <span className="text-[10px] text-slate-400">{log.length} entries{isRunning ? ' — updating every 3 s' : ''}</span>
      </div>
      <div className="overflow-auto max-h-96">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-slate-50 sticky top-0 text-[10px] text-slate-500 font-semibold uppercase">
              <th className="px-2 py-1 text-left w-16">Time</th>
              <th className="px-2 py-1 text-left w-8">Ph.</th>
              <th className="px-2 py-1 text-left w-16">Step</th>
              <th className="px-2 py-1 text-left w-32">Subject</th>
              <th className="px-2 py-1 text-left">Detail</th>
            </tr>
          </thead>
          <tbody>
            {log.map((entry, i) => <LogRow key={i} entry={entry} />)}
          </tbody>
        </table>
        <div ref={logEndRef} />
      </div>
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ done, total, label }: { done: number; total: number; label: string }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs text-slate-500 mb-1">
        <span>{label}</span>
        <span>{done}/{total}</span>
      </div>
      <div className="bg-slate-200 rounded h-2 overflow-hidden">
        <div className="bg-blue-600 h-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<CpcJobStatus, string> = {
  pending: 'bg-slate-100 text-slate-500',
  running: 'bg-blue-100 text-blue-700',
  pass1:   'bg-violet-100 text-violet-700',
  pass2:   'bg-cyan-100 text-cyan-700',
  done:    'bg-green-100 text-green-700',
  error:   'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: CpcJobStatus }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_COLORS[status]}`}>
      {status.toUpperCase()}
    </span>
  );
}

// ── History selector ─────────────────────────────────────────────────────────

const HIST_STATUS_CLS: Record<string, string> = {
  done:    'bg-green-100 text-green-700',
  error:   'bg-red-100 text-red-700',
  pending: 'bg-slate-100 text-slate-500',
  running: 'bg-blue-100 text-blue-700',
  pass1:   'bg-violet-100 text-violet-700',
  pass2:   'bg-cyan-100 text-cyan-700',
};

function HistorySelector({
  history, selectedJobId, loading, onChange,
}: {
  history: JobHistoryEntry[];
  selectedJobId: string;
  loading: boolean;
  onChange: (jobId: string) => void;
}) {
  if (loading) return <p className="text-[11px] text-slate-400 mb-2">Loading history…</p>;
  if (!history.length) return null;
  return (
    <div className="flex items-center gap-2 mb-3 flex-shrink-0 flex-wrap">
      <span className="text-[11px] text-slate-500 font-medium whitespace-nowrap">Run:</span>
      <div className="flex flex-wrap gap-1.5">
        {history.map(entry => {
          const active = entry.job_id === selectedJobId;
          const dt = new Date(entry.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
          const statusCls = HIST_STATUS_CLS[entry.status] ?? 'bg-slate-100 text-slate-500';
          return (
            <button
              key={entry.job_id}
              onClick={() => onChange(entry.job_id)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded border text-[11px] transition-colors ${
                active
                  ? 'border-blue-400 bg-blue-50 text-blue-800 font-semibold'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400'
              }`}
              title={entry.error ?? undefined}
            >
              <span>{dt}</span>
              <span className={`px-1 py-0.5 rounded text-[10px] font-semibold ${statusCls}`}>{entry.status}</span>
              {entry.status === 'done' && (
                <span className="text-slate-400">{entry.class_count}×{entry.feature_count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── CPC Table view ────────────────────────────────────────────────────────────

interface CpcTableViewProps {
  table: CpcTable;
  selectedClasses: Set<string>;
  onToggleClass: (cls: string) => void;
  onDownload: () => void;
}

function CpcTableView({ table, selectedClasses, onToggleClass, onDownload }: CpcTableViewProps) {
  const { t } = useTranslation();
  const [showQueries, setShowQueries] = useState(false);
  const features = table.features;
  const runDate = new Date(table.generated_at).toLocaleString();

  const allChecked = table.classes.length > 0 && table.classes.every(r => selectedClasses.has(r.cpc_class));

  const toggleAll = () => {
    if (allChecked) {
      table.classes.forEach(r => onToggleClass(r.cpc_class));
    } else {
      table.classes.filter(r => !selectedClasses.has(r.cpc_class)).forEach(r => onToggleClass(r.cpc_class));
    }
  };

  return (
    <div className="h-full flex flex-col gap-2">
      {/* Banner */}
      <div className="flex items-center justify-between flex-shrink-0 flex-wrap gap-2">
        <span className="text-[11px] text-slate-400">{t('cpcSearch.done')} {runDate}</span>
        <div className="flex items-center gap-3">
          {selectedClasses.size > 0 && (
            <button
              className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={onDownload}
            >
              {t('cpcSearch.downloadBtn', { count: selectedClasses.size })}
            </button>
          )}
          <button
            className="text-[11px] text-blue-600 hover:underline"
            onClick={() => setShowQueries(v => !v)}
          >
            {showQueries ? t('cpcSearch.hideQueries') : t('cpcSearch.showQueries')}
          </button>
        </div>
      </div>

      {/* CQL queries panel */}
      {showQueries && (
        <div className="flex-shrink-0 border border-slate-200 rounded p-2 bg-slate-50">
          <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">{t('cpcSearch.queriesTitle')}</p>
          <table className="w-full text-[11px]">
            <tbody>
              {features.map(f => (
                <tr key={f.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-0.5 pr-3 font-medium text-slate-700 whitespace-nowrap w-40">{f.name}</td>
                  <td className="py-0.5 font-mono text-violet-700 break-all">{f.final_query || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Matrix */}
      {!table.classes.length ? (
        <p className="text-xs text-slate-400">{t('cpcSearch.tableEmpty')}</p>
      ) : (
        <div className="flex-1 min-h-0 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 sticky top-0">
                <th className="px-2 py-1.5 border border-slate-200 w-7 text-center">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    className="cursor-pointer"
                    title={t('cpcSearch.selectAll', 'Select all')}
                  />
                </th>
                <th className="text-left px-3 py-1.5 border border-slate-200 font-semibold text-slate-600 whitespace-nowrap">
                  {t('cpcSearch.colClass')}
                </th>
                {features.map(f => (
                  <th key={f.id} className="px-3 py-1.5 border border-slate-200 font-semibold text-slate-600 text-center min-w-[72px]" title={f.final_query || f.name}>
                    {f.name.length > 18 ? f.name.slice(0, 17) + '…' : f.name}
                  </th>
                ))}
                <th className="px-3 py-1.5 border border-slate-200 font-semibold text-slate-500 text-center whitespace-nowrap" title={t('cpcSearch.colDocCountTitle')}>
                  {t('cpcSearch.colDocCount')}
                </th>
              </tr>
            </thead>
            <tbody>
              {table.classes.map((row, i) => (
                <tr
                  key={row.cpc_class}
                  className={`${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} ${selectedClasses.has(row.cpc_class) ? 'ring-1 ring-inset ring-blue-300' : ''}`}
                >
                  <td className="px-2 py-1 border border-slate-200 text-center">
                    <input
                      type="checkbox"
                      checked={selectedClasses.has(row.cpc_class)}
                      onChange={() => onToggleClass(row.cpc_class)}
                      className="cursor-pointer"
                    />
                  </td>
                  <td className="px-3 py-1 border border-slate-200 font-mono font-semibold text-slate-700 whitespace-nowrap">
                    {row.cpc_class}
                  </td>
                  {features.map(f => {
                    const score = Math.round(row.relevance_by_feature[f.id] ?? 0);
                    const high = score >= 60;
                    return (
                      <td
                        key={f.id}
                        className="px-3 py-1 border border-slate-200 text-center font-medium"
                        style={{
                          background: score >= 25 ? `rgba(37,99,235,${(score / 100).toFixed(2)})` : undefined,
                          color: high ? '#fff' : undefined,
                        }}
                        title={`${f.name}: ${score}`}
                      >
                        {score}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1 border border-slate-200 text-center text-slate-500 whitespace-nowrap">
                    {row.doc_count != null ? row.doc_count.toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

const CpcSearchPage: React.FC = () => {
  const { subProjectId } = useParams<{ subProjectId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<Tab>('features');
  const [loading, setLoading] = useState(true);
  const [spName, setSpName] = useState('');
  const [projectId, setProjectId] = useState<string | undefined>();
  const [kbId, setKbId] = useState<string | undefined>();
  const [kbName, setKbName] = useState('');

  const {
    features,
    isLoading: featuresLoading,
    error: featuresError,
    loadFeaturesList,
  } = useFeaturesListStore();

  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null);
  const [cpcTable, setCpcTable] = useState<CpcTable | null>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // History
  const [history, setHistory] = useState<JobHistoryEntry[]>([]);
  const [selectedHistoryJobId, setSelectedHistoryJobId] = useState<string>('');
  const [historyLoading, setHistoryLoading] = useState(false);

  // Download modal state
  const [selectedClasses, setSelectedClasses] = useState<Set<string>>(new Set());
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const handleToggleClass = useCallback((cls: string) => {
    setSelectedClasses(prev => {
      const next = new Set(prev);
      if (next.has(cls)) next.delete(cls); else next.add(cls);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!subProjectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sp = await subProjectService.getById(subProjectId);
        if (cancelled) return;
        setSpName(sp.name || '');
        setProjectId(sp.project_id);
        const kbIds = (sp as any).kb_ids || [];
        if (kbIds[0]) {
          setKbId(kbIds[0]);
          try {
            const kb = await knowledgeBaseService.getById(kbIds[0]);
            setKbName(kb.name || '');
          } catch { /* ignore */ }
        }
      } catch (e) {
        console.error('CpcSearchPage: failed to load context', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subProjectId]);

  useEffect(() => {
    if (subProjectId) loadFeaturesList(subProjectId);
  }, [subProjectId, loadFeaturesList]);

  // Load history on mount (and after a job completes)
  const refreshHistory = useCallback(async (autoSwitchTab = false) => {
    if (!subProjectId) return;
    setHistoryLoading(true);
    try {
      const entries = await cpcSearchService.listHistory(subProjectId);
      setHistory(entries);
      // Auto-select the most recent done job if none is selected yet
      if (!selectedHistoryJobId) {
        const latest = entries.find(e => e.status === 'done');
        if (latest) {
          setSelectedHistoryJobId(latest.job_id);
          if (autoSwitchTab) setActiveTab('table');
        }
      }
    } catch { /* ignore */ } finally {
      setHistoryLoading(false);
    }
  }, [subProjectId, selectedHistoryJobId]);

  useEffect(() => { refreshHistory(true); }, [subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load table when selected history entry changes
  useEffect(() => {
    if (!subProjectId || !selectedHistoryJobId) return;
    setTableLoading(true);
    setTableError('');
    cpcSearchService.getResult(subProjectId, selectedHistoryJobId)
      .then(table => { setCpcTable(table); setTableLoading(false); })
      .catch(() => { setCpcTable(null); setTableLoading(false); setTableError(t('cpcSearch.tableLoadError', 'Could not load result for this run.')); });
  }, [subProjectId, selectedHistoryJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopPoll = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const poll = useCallback(async (jid: string) => {
    if (!subProjectId) return;
    try {
      const status = await cpcSearchService.getStatus(subProjectId, jid);
      setJobStatus(status);
      if (status.status === 'done') {
        stopPoll();
        const table = await cpcSearchService.getResult(subProjectId, jid);
        setCpcTable(table);
        setSelectedHistoryJobId(jid);
        setActiveTab('table');
        refreshHistory();
      } else if (status.status === 'error') {
        stopPoll();
      }
    } catch (e) {
      console.warn('CPC poll error:', e);
    }
  }, [subProjectId, stopPoll, refreshHistory]);

  useEffect(() => {
    if (!jobId) return;
    poll(jobId);
    pollRef.current = setInterval(() => poll(jobId), POLL_INTERVAL_MS);
    return stopPoll;
  }, [jobId, poll, stopPoll]);

  const handleStart = async () => {
    if (!subProjectId) return;
    setStartError('');
    setStarting(true);
    try {
      const res = await cpcSearchService.startJob(subProjectId);
      setJobId(res.job_id);
      setActiveTab('progress');
    } catch (e: any) {
      setStartError(e?.response?.data?.detail || 'Failed to start job.');
    } finally {
      setStarting(false);
    }
  };

  const includedFeatures = features.filter(f => f.included_in_search);
  const isRunning = jobStatus && !['done', 'error'].includes(jobStatus.status);
  const showMetrics = user?.role === 'SysAdmin' || user?.role === 'DevOps';

  const tabCls = (tab: Tab) =>
    `px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
      activeTab === tab
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-slate-500 hover:text-slate-700'
    }`;

  if (!subProjectId) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-slate-400 text-sm">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <WorkspaceHeader
        subProjectName={spName}
        fieldLabel={t('cpcSearch.pageTitle')}
        kbName={kbName || undefined}
        onBackToOverview={() => navigate(`/scv2/sub-project/${subProjectId}`)}
        onOpenPilot={() => {}}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ── Left pane — chatbot ── */}
        <div className="w-[30%] flex-shrink-0 flex flex-col border-r border-slate-200 min-h-0 overflow-hidden">
          <SpeakingChatbotV2Assistant
            projectId={projectId}
            entityType="sub-project"
            entityId={subProjectId}
            entityName={spName}
            fieldConfig={{}}
            fieldLabels={{}}
            kbId={kbId}
            kbName={kbName}
            projectName={spName}
            documentIds={[]}
            language={i18n.language as 'en' | 'fr'}
            activeTab="metadata"
            activeField="cpc_search"
            onFieldUpdate={() => {}}
            getFieldValue={() => ''}
            onContextFieldChanged={() => {}}
            onExtractionResults={() => {}}
            onPilotHandoff={() => navigate(`/scv2/sub-project/${subProjectId}`)}
            showMetrics={showMetrics}
            onDraftUpdated={() => {}}
            onFtoUpdated={() => {}}
          />
        </div>

        {/* ── Right pane ── */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex items-center gap-0 border-b border-slate-200 bg-slate-50 flex-shrink-0 px-2">
            <button className={tabCls('features')} onClick={() => setActiveTab('features')}>{t('cpcSearch.tabFeatures')}</button>
            <button className={tabCls('progress')} onClick={() => setActiveTab('progress')}>{t('cpcSearch.tabProgress')}</button>
            <button className={tabCls('table')} onClick={() => setActiveTab('table')}>{t('cpcSearch.tabTable')}</button>
          </div>

          <div className="flex-1 min-h-0 overflow-auto p-4">

            {/* Features tab */}
            {activeTab === 'features' && (
              <div>
                {featuresLoading && <p className="text-xs text-slate-400">{t('cpcSearch.loadingFeatures')}</p>}
                {featuresError && <p className="text-xs text-red-600">{t('cpcSearch.errorLoadingFeatures')}</p>}
                {!featuresLoading && !featuresError && includedFeatures.length === 0 && (
                  <div>
                    <p className="text-xs text-slate-500">{t('cpcSearch.noFeatures')}</p>
                    <p className="text-xs text-slate-400 mt-1">{t('cpcSearch.noFeaturesHint')}</p>
                  </div>
                )}
                {!featuresLoading && includedFeatures.length > 0 && (
                  <>
                    <p className="text-xs text-slate-400 mb-3">{t('cpcSearch.featuresCount', { count: includedFeatures.length })}</p>
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-50">
                          <th className="text-left px-2 py-1.5 border border-slate-200 w-7 text-slate-500">#</th>
                          <th className="text-left px-2 py-1.5 border border-slate-200 w-1/5 text-slate-600 font-semibold">Name</th>
                          <th className="text-left px-2 py-1.5 border border-slate-200 text-slate-600 font-semibold">Description</th>
                          <th className="text-left px-2 py-1.5 border border-slate-200 w-1/3 text-slate-600 font-semibold">Keywords</th>
                        </tr>
                      </thead>
                      <tbody>
                        {includedFeatures.map((f, i) => (
                          <tr key={f.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                            <td className="px-2 py-1 border border-slate-200 text-center text-slate-400">{i + 1}</td>
                            <td className="px-2 py-1 border border-slate-200 font-medium text-slate-700">{f.feature_name}</td>
                            <td className="px-2 py-1 border border-slate-200 text-slate-500">{f.feature_description}</td>
                            <td className="px-2 py-1 border border-slate-200 font-mono text-violet-700 text-[11px]">{f.keywords}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            )}

            {/* Progress tab */}
            {activeTab === 'progress' && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-4">{t('cpcSearch.progressTitle')}</h3>
                {!jobId && (
                  <div>
                    {includedFeatures.length === 0 ? (
                      <p className="text-xs text-slate-500">{t('cpcSearch.noFeatures')}</p>
                    ) : (
                      <>
                        <p className="text-xs text-slate-600 mb-3">{t('cpcSearch.featuresCount', { count: includedFeatures.length })}</p>
                        {startError && <p className="text-xs text-red-600 mb-2">{startError}</p>}
                        <button
                          className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          onClick={handleStart}
                          disabled={starting}
                        >
                          {starting ? t('cpcSearch.startingBtn') : t('cpcSearch.startBtn')}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {jobStatus && (
                  <div>
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <StatusBadge status={jobStatus.status} />
                      {isRunning && (
                        <span className="flex items-center gap-1 text-[11px] text-blue-600">
                          <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                          running
                        </span>
                      )}
                      <span className="text-xs text-slate-600 flex-1">{jobStatus.progress_message}</span>
                    </div>
                    {jobStatus.pass1_total > 0 && (
                      <ProgressBar done={jobStatus.pass1_done} total={jobStatus.pass1_total} label={t('cpcSearch.pass1Label')} />
                    )}
                    {jobStatus.pass2_total > 0 && (
                      <ProgressBar done={jobStatus.pass2_done} total={jobStatus.pass2_total} label={t('cpcSearch.pass2Label')} />
                    )}
                    <LogTable log={jobStatus.log ?? []} isRunning={!!isRunning} />
                    {jobStatus.status === 'error' && (
                      <p className="text-xs text-red-600 mt-3">{t('cpcSearch.jobError')}: {jobStatus.error}</p>
                    )}
                    {jobStatus.status === 'done' && (
                      <div className="flex items-center gap-3 mt-4">
                        <span className="text-xs text-green-700 font-medium">{t('cpcSearch.done')}</span>
                        <button
                          className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                          onClick={() => setActiveTab('table')}
                        >
                          {t('cpcSearch.tabTable')} &rarr;
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CPC Table tab */}
            {activeTab === 'table' && (
              <div className="h-full flex flex-col">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex-shrink-0">{t('cpcSearch.tableTitle')}</h3>
                <HistorySelector
                  history={history}
                  selectedJobId={selectedHistoryJobId}
                  loading={historyLoading}
                  onChange={(jobId) => { setSelectedHistoryJobId(jobId); setSelectedClasses(new Set()); }}
                />
                {tableLoading && (
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                    {t('common.loading', 'Loading...')}
                  </div>
                )}
                {!tableLoading && tableError && (
                  <p className="text-xs text-red-500">{tableError}</p>
                )}
                {!tableLoading && !tableError && !cpcTable && !isRunning && (
                  <p className="text-xs text-slate-400">{t('cpcSearch.noJobYet')}</p>
                )}
                {!tableLoading && !cpcTable && isRunning && (
                  <p className="text-xs text-slate-400">{t('cpcSearch.jobRunning')}</p>
                )}
                {!tableLoading && cpcTable && (
                  <div className="flex-1 min-h-0">
                    <CpcTableView
                      table={cpcTable}
                      selectedClasses={selectedClasses}
                      onToggleClass={handleToggleClass}
                      onDownload={() => setShowDownloadModal(true)}
                    />
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>

      {showDownloadModal && subProjectId && (
        <CpcDownloadModal
          subProjectId={subProjectId}
          selectedClasses={Array.from(selectedClasses)}
          onClose={() => setShowDownloadModal(false)}
        />
      )}
    </div>
  );
};

export default CpcSearchPage;
